import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import styles from "./filter-line-component.module.scss";
import ChevronXSIcon from "../../../icons/chevron-xs.icon";
import CheckIcon from "../../../icons/check.icon";
import CrossIcon from "../../../icons/cross.icon";
import { SelectedFilterDropdown } from "./selected-filter-dropdown";

const getFilterDisplayText = (label, value, type) => {
  const hasValue = type === "radio-range" || type === "checkbox-range"
    ? Array.isArray(value) && value?.filter(i => i.value)?.length > 0
    : value !== null && value !== undefined;

  if (!hasValue) {
    return label;
  }

  if (type === "radio-range") {
    if (Array.isArray(value)) {
      const selectedItem = value.find(i => i.value);
      return selectedItem ? `${label}: ${selectedItem.label}` : `${label}: ${value}`;
    }
    return `${label}: ${value}`;
  }

  if (type === "checkbox-range") {
    if (Array.isArray(value)) {
      const selectedItems = value.filter(i => i.value).map(i => i.label);
      return selectedItems.length > 0 ? `${label}: ${selectedItems.join(", ")}` : `${label}: ${value}`;
    }
    return `${label}: ${value}`;
  }

  return `${label}: ${value}`;
};

export const LeftSide = ({ selectedFilters = [], setSelectedFilters }) => {
  if (!selectedFilters || selectedFilters.length === 0) {
    return <div className={styles.leftSideContainer}></div>;
  }

  return (
    <div className={styles.leftSideContainer} role="group" aria-label="Filter controls">
      <div className={styles.leftSideLabel}>
        Filtered by
      </div>
      {selectedFilters.map((filter) => {
        const displayText = getFilterDisplayText(filter.label, filter.value, filter.type);
        
        // Check if this is a Distributor filter
        const isDistributor = filter.key === "distributor" || filter.label?.toLowerCase() === "distributor";
        
        // Check if this is a Renewal filter
        const isRenewal = filter.key === "renewal" || filter.label?.toLowerCase() === "renewal";
        
        // Check if this is Auto Renewal or Oversubscribed filter
        const isAutoRenewal = filter.key === "autoRenewal" || filter.label?.toLowerCase() === "auto renewal";
        const isOversubscribed = filter.key === "overSubscription" || filter.key === "oversubscribed" || filter.label?.toLowerCase() === "oversubscribed";
        
        // Check if this is Sales Rep filter
        const isSalesRep = filter.key === "salesRep" || filter.label?.toLowerCase() === "sales rep";
        
        // Check if this is Tags filter
        const isTags = filter.key === "tags" || filter.label?.toLowerCase() === "tags";
        
        // Check if this is Integrations filter
        const isIntegrations = filter.key === "integrations" || filter.label?.toLowerCase() === "integrations";
        
        let options = [];
        
        if (isDistributor) {
          options = [
            { 
              id: "highground", 
              label: "HighGround", 
              value: "highground",
              icon: <div className={styles.distributorIcon}>HG</div>
            },
            { 
              id: "portland", 
              label: "Portland", 
              value: "portland",
              icon: <div className={styles.distributorIcon}>PT</div>
            },
            { 
              id: "brigantia", 
              label: "Brigantia", 
              value: "brigantia",
              icon: <div className={styles.distributorIcon}>BR</div>
            },
            { 
              id: "virosafe", 
              label: "Virosafe", 
              value: "virosafe",
              icon: <div className={styles.distributorIcon}>VS</div>
            },
          ];
        } else if (isRenewal) {
          options = [
            { id: "next-60-days", label: "Next 60 days", value: "next-60-days" },
            { id: "next-30-days", label: "Next 30 days", value: "next-30-days" },
            { id: "custom-date", label: "Custom Date", value: "custom-date" },
          ];
        } else if (isSalesRep) {
          options = [
            { id: "robin-smit", label: "Robin Smit", value: "robin-smit" },
            { id: "lili-james", label: "Lili James", value: "lili-james" },
            { id: "mika-walton", label: "Mika Walton", value: "mika-walton" },
            { id: "ellie-brown", label: "Ellie Brown", value: "ellie-brown" },
          ];
        } else if (isTags) {
          options = [
            { id: "high-priority", label: "High Priority", value: "high-priority" },
            { id: "medium-priority", label: "Medium Priority", value: "medium-priority" },
            { id: "referral", label: "Referral", value: "referral" },
          ];
        } else if (isIntegrations) {
          options = [
            { id: "psa", label: "PSA", value: "psa" },
            { id: "rmm", label: "RMM", value: "rmm" },
            { id: "lighthouse", label: "Lighthouse", value: "lighthouse" },
          ];
        } else if (isAutoRenewal || isOversubscribed) {
          options = [
            { id: "yes", label: "Yes", value: "yes" },
            { id: "no", label: "No", value: "no" },
          ];
        } else {
          options = [
            { id: "yes", label: "Yes", value: "yes" },
            { id: "no", label: "No", value: "no" },
          ];
        }

        return (
          <SelectedFilterDropdown
            key={filter.key}
            options={options}
            selectedValue={isDistributor ? (Array.isArray(filter.value) ? filter.value : []) : filter.value}
            onSelect={(value) => {
              if (setSelectedFilters) {
                setSelectedFilters((prevFilters) =>
                  prevFilters.map((f) =>
                    f.key === filter.key ? { ...f, value } : f
                  )
                );
              }
            }}
            displayText={displayText}
            variant="chip"
            headerTitle={isDistributor ? "Distributors" : null}
            multiple={isDistributor}
          />
        );
      })}
    </div>
  );
};

export const RightSide = () => {
  const [selectedOption, setSelectedOption] = useState("Last 90 Days");

  const options = [
    "Last 7 Days",
    "Last 30 Days",
    "Last 90 Days",
    "Last 365 Days",
  ];

  const handleSelect = (option) => {
    setSelectedOption(option);
  };

  return (
    <div className={styles.rightSideContainer}>
      <Dropdown onSelect={handleSelect}>
        <Dropdown.Toggle as="div" className={styles.rightSideButton}>
          <div className={styles.rightSideButtonInner}>
            <div className={styles.rightSideButtonTextWrapper}>
              <div className={styles.rightSideButtonText}>
                {selectedOption}
              </div>
            </div>
            <div className={styles.rightSideButtonIcon}>
              <ChevronXSIcon width={9} style={{ color: 'var(--grayscale-gray-80)' }} />
            </div>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles.rightSideDropdown}>
          {options.map((option) => (
            <Dropdown.Item
              key={option}
              eventKey={option}
              className={styles.rightSideOption}
              active={selectedOption === option}
            >
              <div className={styles.rightSideOptionContent}>
                {selectedOption === option ? (
                  <>
                    <CheckIcon className={styles.rightSideOptionCheck} />
                    <span>{option}</span>
                  </>
                ) : (
                  <span className={styles.rightSideOptionText}>{option}</span>
                )}
              </div>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

const getOptionLabel = (value, filterKey) => {
  // Map values to labels based on filter type
  const distributorLabels = {
    highground: "HighGround",
    portland: "Portland",
    brigantia: "Brigantia",
    virosafe: "Virosafe",
  };
  
  const renewalLabels = {
    "next-60-days": "Next 60 days",
    "next-30-days": "Next 30 days",
    "custom-date": "Custom Date",
  };
  
  const salesRepLabels = {
    "robin-smit": "Robin Smit",
    "lili-james": "Lili James",
    "mika-walton": "Mika Walton",
    "ellie-brown": "Ellie Brown",
  };
  
  const tagsLabels = {
    "high-priority": "High Priority",
    "medium-priority": "Medium Priority",
    "referral": "Referral",
  };
  
  const integrationsLabels = {
    "psa": "PSA",
    "rmm": "RMM",
    "lighthouse": "Lighthouse",
  };
  
  const isDistributor = filterKey === "distributor";
  const isRenewal = filterKey === "renewal";
  const isSalesRep = filterKey === "salesRep";
  const isTags = filterKey === "tags";
  const isIntegrations = filterKey === "integrations";
  
  if (isDistributor && distributorLabels[value]) {
    return distributorLabels[value];
  } else if (isRenewal && renewalLabels[value]) {
    return renewalLabels[value];
  } else if (isSalesRep && salesRepLabels[value]) {
    return salesRepLabels[value];
  } else if (isTags && tagsLabels[value]) {
    return tagsLabels[value];
  } else if (isIntegrations && integrationsLabels[value]) {
    return integrationsLabels[value];
  }
  
  // Default: capitalize first letter or return as is
  return value && typeof value === 'string' 
    ? value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ')
    : value;
};

const generateChipsFromFilters = (selectedFilters) => {
  const chips = [];
  
  selectedFilters.forEach((filter) => {
    if (filter.value === null || filter.value === undefined) {
      return;
    }
    
    const isDistributor = filter.key === "distributor" || filter.label?.toLowerCase() === "distributor";
    
    if (isDistributor && Array.isArray(filter.value) && filter.value.length > 0) {
      // For distributor, show all selected distributors in one chip, separated by commas
      const selectedLabels = filter.value.map(val => getOptionLabel(val, filter.key));
      chips.push({
        id: filter.key,
        label: `${filter.label}: ${selectedLabels.join(", ")}`,
        filterKey: filter.key,
        filterValue: filter.value, // Keep array for removal logic
      });
    } else if (Array.isArray(filter.value) && filter.value.length > 0) {
      // For other filters with multiple selections, show all in one chip
      const selectedLabels = filter.value.map(val => getOptionLabel(val, filter.key));
      chips.push({
        id: filter.key,
        label: `${filter.label}: ${selectedLabels.join(", ")}`,
        filterKey: filter.key,
      });
    } else if (filter.value !== null && filter.value !== undefined) {
      // For single selection filters
      const optionLabel = getOptionLabel(filter.value, filter.key);
      chips.push({
        id: filter.key,
        label: `${filter.label}: ${optionLabel}`,
        filterKey: filter.key,
      });
    }
  });
  
  return chips;
};

export const FilterLineComponent = ({ selectedFilters = [], setSelectedFilters, isDistributorView = false }) => {
  return (
    <div>
      <div className={styles.filterLineComponent}>
        <LeftSide selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
        {!isDistributorView && <RightSide />}
      </div>
      <SelectedOptionsLine 
        selectedFilters={selectedFilters} 
        setSelectedFilters={setSelectedFilters} 
      />
    </div>
  );
};

export const SelectedOptionsLine = ({ selectedFilters = [], setSelectedFilters }) => {
  const chips = generateChipsFromFilters(selectedFilters);

  const handleRemoveChip = (chip) => {
    if (!setSelectedFilters) return;
    
    // Remove entire filter (since we now show all selections in one chip)
    setSelectedFilters((prevFilters) =>
      prevFilters.filter((f) => f.key !== chip.filterKey)
    );
  };

  const handleClearAll = () => {
    if (setSelectedFilters) {
      setSelectedFilters([]);
    }
  };

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className={styles.selectedOptionsLine} role="group" aria-label="Active filters">
      {chips.map((chip) => (
        <div
          key={chip.id}
          className={styles.selectedChip}
          role="group"
          aria-label={`Filter: ${chip.label}`}
        >
          <div className={styles.selectedChipContent}>
            <div className={styles.selectedChipLabel}>{chip.label}</div>
          </div>
          <div className={styles.selectedChipRemove}>
            <button
              className={styles.selectedChipRemoveButton}
              onClick={() => handleRemoveChip(chip)}
              aria-label={`Remove ${chip.label} filter`}
              type="button"
            >
              <CrossIcon 
                width={12} 
                height={12} 
                fill="white"
                className={styles.selectedChipRemoveIcon}
              />
            </button>
          </div>
        </div>
      ))}

      <button
        className={styles.clearAllButton}
        onClick={handleClearAll}
        aria-label="Clear all filters"
        type="button"
      >
        <div className={styles.clearAllButtonContent}>
          <div className={styles.clearAllButtonText}>Clear All</div>
        </div>
      </button>
    </div>
  );
};